/**
 * Kaltura client tools that let the avatar bring the scenario's learner
 * document up on screen mid-conversation, browse it, read it, and put it away.
 *
 * Kaltura's own pushed-media ("Present media") path is deliberately gone: it
 * only ever emitted empty `content-gallery` widgets that blanked the content
 * pane. Instead the tools below are created with `config.type: "client"`, so
 * when the brain calls one the handler runs in the learner's browser
 * (`KalturaAgentFrame`) and drives our own split-screen layout and PDF viewer.
 *
 * No tool takes a document argument — each scenario presents exactly one
 * document, the one configured as its display source. Same contract as D-ID.
 */
import {
  CLOSE_DOCUMENT_TOOL,
  PRESENT_DOCUMENT_TOOL,
  READ_PAGE_TOOL,
  SHOW_PAGE_TOOL,
  presentationInstruction,
} from "../presentationTools";

// Provider-prefixed aliases kept for call sites; the contract itself is shared.
export const KALTURA_PRESENT_DOCUMENT_TOOL = PRESENT_DOCUMENT_TOOL;
export const KALTURA_CLOSE_DOCUMENT_TOOL = CLOSE_DOCUMENT_TOOL;
export const KALTURA_SHOW_PAGE_TOOL = SHOW_PAGE_TOOL;
export const KALTURA_READ_PAGE_TOOL = READ_PAGE_TOOL;

/** Tools attached whenever the capability is on. */
const BASE_TOOLS = [KALTURA_PRESENT_DOCUMENT_TOOL, KALTURA_CLOSE_DOCUMENT_TOOL];
/** Paging tools only make sense for a PDF, the one kind our viewer controls. */
const PDF_TOOLS = [KALTURA_SHOW_PAGE_TOOL, KALTURA_READ_PAGE_TOOL];

/**
 * Kaltura needs an explicit tool budget. Its client tools are silent segments;
 * without a stop-and-speak rule the brain can keep calling them and starve the
 * spoken part of the turn. D-ID has a different tool runtime and keeps the
 * provider-neutral instruction unchanged.
 */
export function kalturaPresentationInstruction(pageable: boolean): string {
  const shared = presentationInstruction(pageable);
  if (!pageable) return shared;
  return (
    shared +
    " KALTURA DOCUMENT TOOL BUDGET: in one turn, call show_page at most once and read_current_page at most once. Never repeat either tool to confirm or retry it. After the document tool result, always finish the turn with 1-3 spoken sentences to the learner; a tool-only silent turn is a failure. If a tool reports an error, explain it briefly in words instead of calling the tool again in the same turn."
  );
}

export interface ReconciledKalturaTools {
  /** Tool ids to attach to the intellect (empty when the capability is off). */
  toolIds: string[];
}

/**
 * Kaltura's `/v1/tool/*` namespace is partner-level, not per-agent, and tool
 * names are unique within it: the four tools above exist once for the partner
 * and every scenario attaches the same ids. So we never create or delete them —
 * minting from a save is what produced
 * `Tool with name 'present_document' already exists` on the second scenario.
 *
 * Ids read back from `/v1/tool/list` (all `type: "client"`, the paging pair
 * blocking). Re-read them with the `tool_list` action if they ever change.
 */
const KALTURA_TOOL_IDS: Record<string, string> = {
  [KALTURA_PRESENT_DOCUMENT_TOOL]: "195d4174-26ab-4ee4-ba19-c1f42ebdad50",
  [KALTURA_CLOSE_DOCUMENT_TOOL]: "251afc67-2cf4-4338-b84a-5a6c532536b1",
  [KALTURA_SHOW_PAGE_TOOL]: "c03875a6-e7ac-44e6-9c15-8e84e1567439",
  [KALTURA_READ_PAGE_TOOL]: "28a6852b-93a4-4a01-9907-ce8fc4bfdc09",
};

/**
 * Select which of the partner's presentation tools this scenario attaches.
 * Pure — no upstream call, nothing to clean up: turning the toggle off just
 * attaches an empty `tool_ids`, leaving the shared tools for other scenarios.
 */
export function reconcileKalturaPresentationTools(input: {
  enabled: boolean;
  /** Whether the display source is a PDF (enables the paging tools). */
  pageable?: boolean;
}): ReconciledKalturaTools {
  if (!input.enabled) return { toolIds: [] };
  const names = input.pageable ? [...BASE_TOOLS, ...PDF_TOOLS] : BASE_TOOLS;
  return { toolIds: names.map((n) => KALTURA_TOOL_IDS[n]).filter(Boolean) };
}
